import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CreditCard, Loader2, XCircle } from "lucide-react";
import { api } from "../lib/api";
import { useLanguage } from "../context/LanguageContext";
import { usePaymentStatus } from "../hooks/usePaymentStatus";
import { readPendingOrder } from "../lib/orderStorage";
import { reportError } from "../lib/reportError";
import { PaymentStatusPanel } from "../components/payment/PaymentStatusPanel";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const payment = usePaymentStatus(params.get("session_id"));
  return <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6"><PaymentStatusPanel {...payment} /></div>;
}

export function PaymentCancel() {
  const { t } = useLanguage();
  const [pending] = useState(readPendingOrder);
  const [retrying, setRetrying] = useState(false);
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  const retry = async () => {
    if (!pending || inFlight.current) return;
    inFlight.current = true;
    setRetrying(true);
    setError("");
    try {
      const { data } = await api.post("/payments/checkout", { order_id: pending.orderId, origin_url: window.location.origin });
      window.location.assign(data.checkout_url);
    } catch (error) {
      reportError("Checkout retry failed", error);
      setError("Could not retry the payment. Please try again or contact the studio.");
    } finally { inFlight.current = false; setRetrying(false); }
  };
  return <div className="mx-auto max-w-2xl px-4 py-24 text-center sm:px-6">
    <div data-testid="payment-cancel-panel">
      <XCircle size={48} className="mx-auto text-[#FF3366]" />
      <h1 className="mt-5 font-display text-3xl font-extrabold text-slate-50">{t("Payment cancelled")}</h1>
      <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-slate-400">{t("No worries — nothing was charged and your confirmed lyric is saved. Whenever you're ready, you can complete the payment online and production starts right away.")}</p>
      {error && <p role="alert" data-testid="payment-retry-error" className="mt-4 text-sm text-rose-300">{t(error)}</p>}
      <div className="mt-8 flex flex-wrap justify-center gap-4">
        {pending && <button data-testid="payment-retry-button" onClick={retry} disabled={retrying} className="btn-primary">
          {retrying ? <Loader2 size={16} className="animate-spin" /> : <CreditCard size={16} />}{t("Complete Payment")}
        </button>}
        <Link to="/order" data-testid="payment-cancel-order-link" className="btn-ghost">{t("Back to Order")}</Link>
      </div>
    </div>
  </div>;
}